export const REDACTED = '[redacted]'

// Field names whose values are credentials: passwords, bearer and subject
// tokens, signed assertions and proofs, and reset links (which embed a token).
// Deliberately does not match descriptive fields such as `tokenType`.
const SENSITIVE_KEY = /password|secret|assertion$|^proof$|^reseturl$|^token$|access_?token|^subject_token$|^authorization$|^privatejwk$/i

const MAX_TEXT_LENGTH = 20_000

function truncate(text: string): string {
  return text.length > MAX_TEXT_LENGTH ? `${text.slice(0, MAX_TEXT_LENGTH)}\n… (${text.length - MAX_TEXT_LENGTH} more characters)` : text
}

function redactValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(redactValue)
  }
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, entry]) => [key, SENSITIVE_KEY.test(key) && entry !== null ? REDACTED : redactValue(entry)]),
    )
  }
  return value
}

export function redactHeaders(headers: Record<string, string>): Record<string, string> {
  return Object.fromEntries(Object.entries(headers).map(([name, value]) => [name, SENSITIVE_KEY.test(name) ? REDACTED : value]))
}

function redactForm(text: string): string {
  const lines = [...new URLSearchParams(text)].map(([name, value]) => `${name}=${SENSITIVE_KEY.test(name) ? REDACTED : value}`)
  return lines.join('\n')
}

/**
 * A request or response body as display text with credentials removed. JSON
 * is pretty-printed, a form body is shown one field per line, and anything
 * else (a CSV upload, a plain-text error) is shown as sent.
 */
export function redactBody(text: string, contentType: string): string {
  if (text === '') {
    return ''
  }
  if (contentType.includes('application/x-www-form-urlencoded')) {
    return redactForm(text)
  }
  try {
    return truncate(JSON.stringify(redactValue(JSON.parse(text)), null, 2))
  } catch {
    return truncate(text)
  }
}
